import { SENSOR_SIGNAL_TYPE } from "@shared/constants";
import { AlertNotificationFilter } from "@shared/interfaces";
import { produce } from "immer";
import { ActionType, createReducer } from "typesafe-actions";

import { AlertStateType } from "../interfaces";
import * as actions from "./actions";

type Action = ActionType<typeof actions>;

const defaultFilter: AlertNotificationFilter = {
  page: 0,
  limit: 15,
  search: "",
};

export const initialState: AlertStateType = {
  alerts: [],
  alertNotifications: [],
  hrAlertNotifications: [],
  rrAlertNotifications: [],
  hrAlertNotificationsTotal: 0,
  rrAlertNotificationsTotal: 0,
  alertNotificationsTotal: 0,
  hrFilter: { ...defaultFilter, signal_type: SENSOR_SIGNAL_TYPE.HR, selector_type: SENSOR_SIGNAL_TYPE.HR },
  rrFilter: { ...defaultFilter, signal_type: SENSOR_SIGNAL_TYPE.RR, selector_type: SENSOR_SIGNAL_TYPE.RR },
  filter: { ...defaultFilter },
};

const reducer = createReducer<AlertStateType, Action>(initialState)
  .handleAction(actions.getAlerts.success, (state, action) =>
    produce(state, (nextState) => {
      nextState.alerts = action.payload;
    }),
  )

  .handleAction(actions.createAlert.success, (state, action) =>
    produce(state, (nextState) => {
      nextState.alerts = action.payload.alerts;
    }),
  )

  .handleAction(actions.createUserAlert.success, (state, action) =>
    produce(state, (nextState) => {
      nextState.alerts = action.payload.alerts;
    }),
  )

  .handleAction(actions.getAlertNotifications.success, (state, action) =>
    produce(state, (nextState) => {
      const { count, rows, selector_type, is_clear } = action.payload;

      let itemsProperty: keyof AlertStateType = "alertNotifications";
      let itemsCountProperty: keyof AlertStateType = "alertNotificationsTotal";

      switch (selector_type) {
        case SENSOR_SIGNAL_TYPE.RR: {
          itemsProperty = "rrAlertNotifications";
          itemsCountProperty = "rrAlertNotificationsTotal";
          break;
        }
        case SENSOR_SIGNAL_TYPE.HR: {
          itemsProperty = "hrAlertNotifications";
          itemsCountProperty = "hrAlertNotificationsTotal";
          break;
        }
      }

      nextState[itemsProperty] = !is_clear ? [...nextState[itemsProperty], ...rows] : [...rows];
      nextState[itemsCountProperty] = count;
    }),
  )
  .handleAction(actions.readAlertNotification.success, (state, action) =>
    produce(state, (nextState) => {
      const alertsHr = nextState.hrAlertNotifications;
      const alertsRr = nextState.rrAlertNotifications;
      const alerts = nextState.alertNotifications;

      const foundAlertHr = alertsHr.find((s) => action.payload.id === s.id);
      if (foundAlertHr) {
        nextState.hrAlertNotifications = alertsHr.filter((hr) => hr.id !== foundAlertHr.id);
      }

      const foundAlertRr = alertsRr.find((s) => action.payload.id === s.id);
      if (foundAlertRr) {
        nextState.rrAlertNotifications = alertsRr.filter((a) => a.id !== foundAlertRr.id);
      }

      const foundAlert = alerts.find((s) => action.payload.id === s.id);
      if (foundAlert) {
        nextState.alertNotifications = alerts.filter((a) => a.id !== foundAlert.id);
      }
    }),
  )
  .handleAction(actions.setFilter, (state, action) =>
    produce(state, (nextState) => {
      const { payload } = action;

      let filterProperty: keyof AlertStateType = "filter";

      switch (payload?.signal_type) {
        case SENSOR_SIGNAL_TYPE.RR: {
          filterProperty = "rrFilter";
          break;
        }
        case SENSOR_SIGNAL_TYPE.HR: {
          filterProperty = "hrFilter";
          break;
        }
      }

      nextState[filterProperty] = payload || { ...defaultFilter };
    }),
  );

export { reducer as AlertReducer };
